import React, { useState } from 'react';
import { ArrowLeftRight, RotateCcw, X } from 'lucide-react';
import type { CompetencyDomain, FrameworkItem, ItemType, Role } from '../../types/framework';
import { ITEM_TYPE_LABEL, PICKS_PER_TYPE, isStandard, questionnaireSize, standardItems, substitutedCount, substitutedInDomain } from '../../utils/framework';
type QuestionPickerProps = {
  role: Role;
  selected: number[];
  onSwap: (fromNo: number, toNo: number) => void;
  onResetAll: () => void;
};
export function QuestionPicker({
  role,
  selected,
  onSwap,
  onResetAll
}: QuestionPickerProps) {
  const [activeId, setActiveId] = useState(role.domains[0]?.id ?? '');
  const [confirmReset, setConfirmReset] = useState(false);
  const activeDomain = role.domains.find(d => d.id === activeId) ?? role.domains[0];
  const totalSwapped = substitutedCount(role, selected);
  const total = questionnaireSize(role);
  return <div className="space-y-5 animate-in fade-in duration-300" data-id="element-118" data-mp="11r8g0pecejiz">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3" data-id="element-119" data-mp="2hiw7xfbecvpk">
        <div data-id="element-120" data-mp="2ck2uto00v6px">
          <h2 className="text-xl font-semibold text-slate-900" data-id="element-121" data-mp="2ckcnxkgd5tzu">
            Review Questions
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-122" data-mp="27suiaj3eb58j">
            A standard set has been chosen for you. Swap any question for an
            alternative.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-id="element-123" data-mp="1ysejhmxlkma0">
          <span className="text-sm text-slate-600" data-id="element-124" data-mp="1ytzeterqk98x">
            <span className="font-semibold text-slate-900" data-id="element-125" data-mp="1tt1wn26q7f7f">
              {selected.length}
            </span>{' '}
            of {total} selected
            {totalSwapped > 0 && <span className="text-slate-500" data-id="element-126" data-mp="1jvngz4smmug6"> · {totalSwapped} swapped</span>}
          </span>
          {totalSwapped > 0 && <button type="button" onClick={() => setConfirmReset(true)} className="inline-flex items-center text-sm font-medium text-slate-700 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-127" data-mp="3j786jp229mp3">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" data-id="element-128" data-mp="1hbhez2nkpe29" />
              Reset all to standard
            </button>}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5" data-id="element-129" data-mp="12nwcnayivive">
        <nav aria-label="Competency domains" className="lg:w-64 shrink-0" data-id="element-130" data-mp="17kedw0un8z2z">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2" data-id="element-131" data-mp="1vaqr9r8nro0y">
            Domains
          </div>
          <ul className="space-y-1 max-h-[520px] overflow-y-auto pr-1" data-id="element-132" data-mp="208scoitk3zfl">
            {role.domains.map(domain => {
            const swapped = substitutedInDomain(domain, selected).length;
            const isActive = domain.id === activeDomain.id;
            return <li key={domain.id} data-id="element-133" data-mp="audsmbthh0qf">
                  <button type="button" onClick={() => setActiveId(domain.id)} aria-current={isActive ? 'true' : undefined} className={`w-full px-3 py-2 rounded-md text-left transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-id="element-134" data-mp="atc3x4jjofq8">
                    <span className="block text-sm truncate" data-id="element-135" data-mp="hbfc1r5ukqp6">
                      {domain.name}
                    </span>
                    <span className={`block text-xs mt-0.5 ${isActive ? 'text-slate-300' : swapped > 0 ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-136" data-mp="cclvfpv9kjks">
                      {swapped > 0 ? `Customised (${swapped} swapped)` : 'Standard'}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <section className="flex-1 min-w-0" aria-labelledby="active-domain" data-id="element-137" data-mp="3ppe6god6840k">
          <h3 id="active-domain" className="text-base font-semibold text-slate-900 mb-4" data-id="element-138" data-mp="320crsjdqvwmu">
            {activeDomain.name}
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-id="element-139" data-mp="20b6g8nz0o053">
            {(['characteristic', 'behaviour'] as ItemType[]).map(type => <TypeColumn key={`${activeDomain.id}-${type}`} domain={activeDomain} type={type} selected={selected} onSwap={onSwap} data-id="element-140" data-mp="24d1e1ihqnve4" />)}
          </div>
        </section>
      </div>

      {confirmReset && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-141" data-mp="2j14op56q7v99">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setConfirmReset(false)} data-id="element-142" data-mp="2nyxqzn4ws549" />
          <div role="alertdialog" aria-modal="true" aria-labelledby="reset-title" className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6" data-id="element-143" data-mp="2s09mg9agrkkd">
            <h2 id="reset-title" className="text-base font-semibold text-slate-900" data-id="element-144" data-mp="12mwbw2brd0at">
              Reset all questions to standard?
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-145" data-mp="v57w9whqsgik">
              Your {totalSwapped} swapped{' '}
              {totalSwapped === 1 ? 'question' : 'questions'} will be replaced
              with the standard set.
            </p>
            <div className="flex justify-end gap-3 mt-6" data-id="element-146" data-mp="lbh9cw19ke4z">
              <button onClick={() => setConfirmReset(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-147" data-mp="gg1x28iwmqao">
                Cancel
              </button>
              <button onClick={() => {
            onResetAll();
            setConfirmReset(false);
          }} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800" data-id="element-148" data-mp="143s8qfwpltyc">
                Reset to standard
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
function TypeColumn({
  domain,
  type,
  selected,
  onSwap
}: {
  domain: CompetencyDomain;
  type: ItemType;
  selected: number[];
  onSwap: (fromNo: number, toNo: number) => void;
}) {
  const [swappingNo, setSwappingNo] = useState<number | null>(null);
  const chosen = domain[type].filter(i => selected.includes(i.no));
  const alternatives = domain[type].filter(i => !selected.includes(i.no));
  const standards = standardItems(domain, type);
  const restoreTarget = (item: FrameworkItem) => standards.find(s => !selected.includes(s.no));
  return <div className="border border-slate-200 rounded-xl p-4 bg-white" data-id="element-149" data-mp="38cfyvvj6q9el">
      <div className="flex items-center justify-between mb-3" data-id="element-150" data-mp="2tmrswh026mkj">
        <h4 className="text-sm font-semibold text-slate-900" data-id="element-151" data-mp="2yk0eysobvar2">
          {ITEM_TYPE_LABEL[type]}
        </h4>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700" data-id="element-152" data-mp="2kkil3yw6d7au">
          {chosen.length} of {PICKS_PER_TYPE}
        </span>
      </div>
      <ul className="space-y-3" data-id="element-153" data-mp="25y23k3cwkpmi">
        {chosen.map(item => {
        const substituted = !isStandard(domain, item);
        const isSwapping = swappingNo === item.no;
        const restore = substituted ? restoreTarget(item) : undefined;
        return <li key={item.no} data-id="element-154" data-mp="2f01kywrtxvpz">
              <div className={`p-3 rounded-lg border ${isSwapping ? 'border-slate-900 ring-1 ring-slate-900' : substituted ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200'}`} data-id="element-155" data-mp="29zx1cxvmkkvj">
                <div className="flex items-center justify-between gap-2 mb-1" data-id="element-156" data-mp="2jycqwwf07m2x">
                  <span className="text-xs font-medium text-slate-500 truncate" data-id="element-157" data-mp="2xozsqxg29s38">
                    {item.competency}
                  </span>
                  {substituted && <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded shrink-0" data-id="element-158" data-mp="2tq0f8og9kn06">
                      Substituted
                    </span>}
                </div>
                <p className="text-sm text-slate-900 leading-snug" data-id="element-159" data-mp="ducdxlh34b09">
                  {item.criterion}
                </p>
                <div className="flex items-center gap-4 mt-2.5" data-id="element-160" data-mp="hveeq6qngv4c">
                  <button type="button" onClick={() => setSwappingNo(isSwapping ? null : item.no)} aria-expanded={isSwapping} className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-slate-900" data-id="element-161" data-mp="mvspvxnjsl6l">
                    {isSwapping ? <>
                        <X className="h-3.5 w-3.5 mr-1" data-id="element-162" data-mp="452ko2ai8mak" /> Cancel
                      </> : <>
                        <ArrowLeftRight className="h-3.5 w-3.5 mr-1" data-id="element-163" data-mp="8xvpsr9vxwn6" /> Swap
                      </>}
                  </button>
                  {restore && !isSwapping && <button type="button" onClick={() => onSwap(item.no, restore.no)} className="inline-flex items-center text-xs font-medium text-indigo-700 hover:text-indigo-900" data-id="element-164" data-mp="3cjtbudn5w3dt">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" data-id="element-165" data-mp="2v2w6xs986t5j" /> Restore default
                    </button>}
                </div>
              </div>

              {isSwapping && <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-200 space-y-2" data-id="element-166" data-mp="2m3936zt0xb9b">
                  <p className="text-xs text-slate-500" data-id="element-167" data-mp="2hbr30dytrepe">
                    Choose a replacement {ITEM_TYPE_LABEL[type]} question:
                  </p>
                  {alternatives.map(alt => <button key={alt.no} type="button" onClick={() => {
              onSwap(item.no, alt.no);
              setSwappingNo(null);
            }} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors" data-id="element-168" data-mp="27emngwzhihhk">
                      <span className="flex items-center justify-between gap-2" data-id="element-169" data-mp="1qu8bwpnc80vx">
                        <span className="text-xs font-medium text-slate-500 truncate" data-id="element-170" data-mp="25k76kifoou6n">
                          {alt.competency}
                        </span>
                        {isStandard(domain, alt) && <span className="text-[11px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded shrink-0" data-id="element-171" data-mp="1cw1ltvomr56t">
                            Standard
                          </span>}
                      </span>
                      <span className="block text-sm text-slate-900 mt-0.5 leading-snug" data-id="element-172" data-mp="1hucuw473pbnz">
                        {alt.criterion}
                      </span>
                    </button>)}
                </div>}
            </li>;
      })}
      </ul>
    </div>;
}